import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type React from 'react';
import { z } from 'zod';

import { type EnhancedToolResult } from '../types';
import { Tool, type ToolConstructorArgs } from './Tool';

import { hasAvailableVideoSkills, hasLoadedVideoSkill } from 'js/src/agent/skills/loadedSkills';
import type { SkillSurface } from 'js/src/agent/skills/types';
import type {
    ConversationMessage,
    ToolCall,
    ToolDefinition,
    ToolExecutionContext,
} from 'js/src/agent/types';
import { captureVideoState, formatVideoStateAsGitDiff } from 'js/src/agent/video-state';
import { AspectRatio, GlobalTemplate } from 'js/src/client';
import { BlankVideoStatusIndicator } from 'js/src/components/video-planner/BlankVideoStatusIndicator';
import { enterCreationStage } from 'js/src/data/agent-activity';
import { selectSketchControlsBrandKitId } from 'js/src/data/sketch-controls/sketch-controls-selectors';
import { applyBlankTemplateToLuminary } from 'js/src/data/video-planner';
import { loadAndApplyBrandKitToLuminary } from 'js/src/luminary/brand-kit-operations';

/** The toolbox is the only surface this tool is registered on. */
const SKILL_SURFACE: SkillSurface = 'sketch';

export const createBlankVideoSchema = z.object({
    title: z.string().describe('Title of the video'),
    // The allowed template ids are per-instance (the templates available to this
    // agent), so the enum is injected in extendParameters rather than declared
    // here; execute() enforces membership.
    template_id: z
        .string()
        .describe(
            'Template ID to use from the available templates. MUST be one of the templates listed in the system message. Never use a non-existent template name.',
        ),
    aspect_ratio: z
        .enum(AspectRatio)
        .describe(
            'Aspect ratio for the video: landscape (16:9 — YouTube, websites), vertical (9:16 — TikTok, Reels, Stories), fourfive (4:5 — Instagram feed), square (1:1 — rarely the best choice).',
        ),
});
export type CreateBlankVideoArgs = z.infer<typeof createBlankVideoSchema>;

interface CreateBlankVideoToolConstructorArgs extends ToolConstructorArgs {
    templateIds?: string[];
    /**
     * Supported aspect ratios per template id. When provided, an unsupported
     * template/aspect-ratio combination is rejected with a corrective error
     * (the prompt's template list no longer spells out per-template support).
     */
    supportedAspectRatiosByTemplateId?: Record<string, string[]>;
    /** Returns the app's current videoId, or null if no video is loaded. */
    getCurrentVideoId?: () => number | null;
    /**
     * Called with the intended video name when a blank video is created. The
     * video is not persisted yet (an empty video is rejected by the backend);
     * the host persists it at turn end, once the agent has added scenes.
     */
    onPrepareVideoCreation?: (name: string) => void;
    /**
     * The conversation's messages, read to see which skills were loaded so
     * creating a video can be rejected until a video-kind skill is among them —
     * the build guidance lives in those skills, so building without one
     * produces the unguided videos the skills exist to prevent. Read from the
     * conversation rather than tracked as calls happen, so a resumed
     * conversation still knows. Omit to skip the gate entirely.
     */
    getConversationMessages?: () => ConversationMessage[];
}

/**
 * Creates a blank, editable video from a template and aspect ratio, building it
 * directly on the agent's (editor) Luminary, then leaves it for the agent to
 * build up with the regular edit tools (add_scene, set_text, etc.). Unlike the
 * plan-based creation tools, this does not run any server-side script/scene
 * generation — the agent composes the video itself.
 *
 * The DB video is not created here: the backend rejects an empty video, so this
 * only sets up the blank video and its intended name; the host persists it at
 * turn end once the agent has added scenes, after which autosave takes over.
 */
export class CreateBlankVideoTool extends Tool<
    typeof createBlankVideoSchema,
    CreateBlankVideoToolConstructorArgs
> {
    protected override readonly schema = createBlankVideoSchema;
    protected override readonly toolName = TOOL_NAMES.createBlankVideo;
    protected override readonly toolDescription = `Create a new blank video using a template and aspect ratio. The video starts empty with no scenes. After calling this, build out the video using the editing tools (${TOOL_NAMES.addScene} first, then ${TOOL_NAMES.setText}, set media, etc.). Call this once at the start of creating a new video; do not call it to edit an existing video.`;

    public override statusIndicator: React.ComponentType<{ toolCall: ToolCall }> | null =
        BlankVideoStatusIndicator;
    private templateIds: string[];
    private supportedAspectRatiosByTemplateId: Record<string, string[]>;
    private getCurrentVideoId?: () => number | null;
    private onPrepareVideoCreation?: (name: string) => void;
    private getConversationMessages?: () => ConversationMessage[];

    public constructor(args: CreateBlankVideoToolConstructorArgs) {
        super(args);
        this.templateIds = args.templateIds || [];
        this.supportedAspectRatiosByTemplateId = args.supportedAspectRatiosByTemplateId || {};
        this.getCurrentVideoId = args.getCurrentVideoId;
        this.onPrepareVideoCreation = args.onPrepareVideoCreation;
        this.getConversationMessages = args.getConversationMessages;
    }

    // template_id's allowed values are the templates available to this agent
    // instance, which the module-level schema can't see. Inject them as the enum
    // the model chooses from; execute() still enforces membership.
    protected override extendParameters(
        parameters: ToolDefinition['function']['parameters'],
    ): ToolDefinition['function']['parameters'] {
        if (this.templateIds.length > 0) {
            (parameters.properties.template_id as { enum?: string[] }).enum = this.templateIds;
        }
        return parameters;
    }

    public getToolDisplayName(): string {
        // This is one step among the build's steps now (not a titled tile), so
        // it reads as a plain action rather than echoing the video title.
        return 'Creating video';
    }

    public get statusIcon(): string {
        return 'movie';
    }

    public async execute(
        args: CreateBlankVideoArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        // This tool is only registered on the sketch surface, where the toolbox
        // offers its recipes.
        const messages = this.getConversationMessages?.();
        if (
            messages &&
            hasAvailableVideoSkills(SKILL_SURFACE) &&
            !hasLoadedVideoSkill(messages, SKILL_SURFACE)
        ) {
            return {
                result: `Error: no recipe has been loaded yet. Before creating a video, pick the best-matching recipe from Available Recipes & Skills and load it with ${TOOL_NAMES.loadSkill} (${SKILL_NAMES.createVideo} when nothing more specific fits), then call ${TOOL_NAMES.createBlankVideo} again.`,
                isSuccess: false,
            };
        }

        if (this.templateIds.length > 0 && !this.templateIds.includes(args.template_id)) {
            return {
                result: `Invalid template_id "${args.template_id}". Must be one of the templates listed in the system message.`,
                isSuccess: false,
            };
        }

        if (args.template_id === (GlobalTemplate.GLOBAL_TEMPLATE_ID as string)) {
            return {
                result: `"${args.template_id}" is a base template with no scene designs of its own and can't be used directly. Pick one of the real templates listed in the system message.`,
                isSuccess: false,
            };
        }

        const supportedAspectRatios = this.supportedAspectRatiosByTemplateId[args.template_id];
        if (supportedAspectRatios && !supportedAspectRatios.includes(args.aspect_ratio)) {
            return {
                result: `Template "${args.template_id}" does not support the "${args.aspect_ratio}" aspect ratio. It supports: ${supportedAspectRatios.join(', ')}. Pick one of those, or a different template.`,
                isSuccess: false,
            };
        }

        // Exactly one video exists at a time. If one already exists, tell the
        // agent to modify or rebuild it rather than make a new one — creating a
        // second would orphan the first. A video "exists" if the app has a
        // persisted videoId OR the agent has already built scenes on the
        // in-memory luminary this turn (before the video is persisted at turn
        // end).
        //
        // The scenes check exists for mid-turn course-correction: within one
        // turn the agent can create_blank_video -> add scenes -> then realize it
        // needs a different template. There's no videoId yet (persistence
        // happens at turn end), but scenes now exist, so without this check the
        // tool would look safe to run again and re-template in place — which
        // throws (canChangeTemplates is false once scene groups exist). Detecting
        // the in-memory video redirects that to rebuild_video, which re-templates
        // safely. (This is not about a pre-loaded video: reopening a saved video
        // sets a videoId, caught by the branch above.)
        const videoAlreadyExists =
            this.getCurrentVideoId?.() != null || this.luminary.scenes.length > 0;
        if (videoAlreadyExists) {
            return {
                result: `A video already exists. There is only one video at a time, so do not create another — modify the current video with the editing tools. To switch templates or start the video over, use the ${TOOL_NAMES.rebuildVideo} tool instead.`,
                isSuccess: false,
            };
        }

        // The agent's luminary IS the editor luminary (one shared
        // LuminaryContextProvider), so we build the blank video directly on it.
        // We deliberately do NOT create the DB video here: the backend rejects
        // an empty video (no scene groups / narrative threads), and a
        // freshly-templated blank video has none yet. Instead we set the
        // intended name and let the host create + persist the video at turn end,
        // once the agent has added scenes. From then on autosave keeps it saved.
        try {
            const luminary = this.luminary;

            await this.dispatch(
                applyBlankTemplateToLuminary({
                    luminary,
                    templateId: args.template_id,
                    aspectRatio: args.aspect_ratio,
                }),
            );

            const brandKitId = selectSketchControlsBrandKitId(this.getState());
            if (brandKitId) {
                await this.dispatch(loadAndApplyBrandKitToLuminary(luminary, brandKitId));
            }

            // Record the intended name now (display-only until the video is
            // persisted at turn end via onPrepareVideoCreation).
            this.onPrepareVideoCreation?.(args.title);

            // Planning is over — the template is chosen and scenes go in from here.
            this.dispatch(enterCreationStage({ stage: 'building' }));

            analytics.track('Sketch: Blank Video Created', {
                title: args.title,
                template_id: args.template_id,
                aspect_ratio: args.aspect_ratio,
                brand_kit_id: brandKitId,
            });

            // Seed the videoStateManager so the first edit tool in the turn diffs
            // against the new video rather than re-capturing the same state. Capture with the
            // manager's Redux accessor (not this tool's) so the seeded snapshot's coverage/pause
            // fidelity matches what the manager captures later — otherwise every voicedriven scene
            // would read as a phantom coverage change on the next diff.
            const captureGetState = context.videoStateManager
                ? context.videoStateManager.getStateAccessor()
                : this.getState;
            const includeColorNames = context.videoStateManager?.getIncludeColorNames() ?? true;
            const snapshot = await captureVideoState(
                luminary,
                undefined,
                captureGetState,
                includeColorNames,
            );
            context.videoStateManager?.setSnapshot(snapshot);

            const summary = JSON.stringify({
                success: 'Created a blank video. Now build it out using the editing tools.',
            });
            // Render in the hosting agent's addressing mode so the seeded state matches every later
            // read (scene_id/scene_group_id for the toolbox, index for legacy).
            const sceneAddressing = context.videoStateManager?.getSceneAddressing() ?? 'index';
            const videoState = formatVideoStateAsGitDiff(snapshot, sceneAddressing);

            return {
                result: `${summary}\n\n${videoState}`,
                isSuccess: true,
            };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return {
                result: `Error creating blank video: ${errorMessage}`,
                isSuccess: false,
            };
        }
    }
}
